// Month grid layout. Pure: no I/O, no DOM, no database.
//
// The publisher backend and the public pages need the same grid, and the
// public one has to render without JavaScript. A shared component cannot span
// a client app and a server template without a framework, and there will not
// be one — so what is shared is this calculation, and the two renderers stay
// thin.
//
// The part worth isolating is multi-day segmentation: an entry crossing a
// week boundary is two segments in two rows, one with an open right edge and
// one with an open left edge. Getting that wrong is invisible until a
// festival lands on the wrong weekend.

const DAY_MS = 86400000;

/**
 * @param stackSingleDays  opt-in, and off by default on purpose. With it,
 *   single-day entries land in `days[i].entries` and cost no lane, which is
 *   what lets a cell carry thirty of them. Without it they become one-column
 *   segments exactly as before.
 *
 *   The flag exists because the backend renderer (`app/views/calendar.js`)
 *   draws from `week.segments` alone. Flipping the default would empty its
 *   month view of everything except festivals, silently — so the caller that
 *   wants the new shape asks for it, and the other one keeps working until it
 *   is ready to move.
 */
export function buildMonth({
  year, month, occurrences = [], weekStartsOn = 1, today = null,
  stackSingleDays = false,
}) {
  const firstOfMonth = Date.UTC(year, month - 1, 1);
  const lastOfMonth = Date.UTC(year, month, 0);

  const lead = (new Date(firstOfMonth).getUTCDay() - weekStartsOn + 7) % 7;
  const trail = 6 - ((new Date(lastOfMonth).getUTCDay() - weekStartsOn + 7) % 7);

  const gridStart = firstOfMonth - lead * DAY_MS;
  const dayCount = Math.round((lastOfMonth + trail * DAY_MS - gridStart) / DAY_MS) + 1;

  const firstIso = iso(firstOfMonth);
  const lastIso = iso(lastOfMonth);

  const weeks = [];
  for (let offset = 0; offset < dayCount; offset += 7) {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const date = iso(gridStart + (offset + i) * DAY_MS);
      days.push({
        date,
        inMonth: date >= firstIso && date <= lastIso,
        isToday: today !== null && date === today,
      });
    }
    weeks.push({ days, segments: [] });
  }

  // Single-day entries do not need lanes. A lane exists so a multi-day bar can
  // hold one row across several columns; an entry that never leaves its own
  // cell can be stacked by the cell in normal flow instead. Keeping the two
  // apart is what removes the ceiling — a day can carry thirty entries because
  // nothing has to allocate thirty grid rows for them.
  const byDate = new Map();
  for (const week of weeks) {
    for (const day of week.days) {
      day.entries = [];
      byDate.set(day.date, day);
    }
  }

  for (const occurrence of occurrences) {
    const spans = Math.max(1, occurrence.spansDays ?? 1);
    const day = byDate.get(occurrence.localDate);

    if (stackSingleDays && spans === 1 && day) {
      day.entries.push(occurrence);
      continue;
    }

    for (const segment of segmentsFor(occurrence, gridStart, dayCount)) {
      weeks[segment.week].segments.push({
        occurrenceId: occurrence.id,
        startColumn: segment.startColumn,
        span: segment.span,
        continuesLeft: segment.continuesLeft,
        continuesRight: segment.continuesRight,
        occurrence,
      });
    }
  }

  // Chronological inside a cell. The caller hands rows in whatever order its
  // query produced; a day cell has to read top to bottom by time.
  for (const day of byDate.values()) {
    day.entries.sort((a, b) => String(a.sortKey ?? '').localeCompare(String(b.sortKey ?? '')));
  }

  for (const week of weeks) {
    week.barLanes = assignLanes(week.segments);
    week.laneCount = week.barLanes; // retained: the app renderer still reads it
  }

  return { weeks };
}

// Which row inside the week each segment sits on. Two events on the same day
// must not land on top of each other, and a multi-day bar has to keep one
// lane for its whole width. Lanes belong here rather than in CSS because the
// day cells span every lane as their background — the renderer needs the row
// count before it can place anything.
function assignLanes(segments) {
  const lanes = []; // lanes[i] = the first free column in lane i

  const ordered = [...segments].sort(
    (a, b) => a.startColumn - b.startColumn || b.span - a.span
  );

  for (const segment of ordered) {
    let lane = lanes.findIndex((freeFrom) => freeFrom <= segment.startColumn);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(0);
    }
    lanes[lane] = segment.startColumn + segment.span;
    segment.lane = lane;
  }

  return lanes.length;
}

function segmentsFor(occurrence, gridStart, dayCount) {
  const startIndex = Math.round(
    (Date.parse(`${occurrence.localDate}T00:00:00Z`) - gridStart) / DAY_MS
  );
  const total = Math.max(1, occurrence.spansDays ?? 1);

  let index = startIndex;
  let remaining = total;

  // An entry can begin before the rendered grid — a festival that started
  // last month. Clip the head; the open left edge says it continues.
  if (index < 0) {
    remaining += index;
    index = 0;
  }

  const segments = [];
  while (remaining > 0 && index < dayCount) {
    const column = index % 7;
    const span = Math.min(remaining, 7 - column, dayCount - index);

    segments.push({
      week: Math.floor(index / 7),
      startColumn: column + 1,
      span,
      continuesLeft: index !== startIndex,
      continuesRight: remaining - span > 0,
    });

    index += span;
    remaining -= span;
  }

  return segments;
}

function iso(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}
